import { SelectionSlot } from '../context/personalisation-typedefs';
import { PersonalisationChoice } from '../gateway/addToCart';
import { fetchImageAsFile } from './addImagesToFormData';
import { supportsWebP } from './webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

interface ImagePropertiesProcessingProps {
  visData: {
    url: string | File;
    file: File | null;
    visualiserSlotMode: string;
    groupId: string;
    lineItemPropertyKey: string;
    label: string;
    positioning?: { x: number; y: number; scale: number } | null;
  }[];
  personalisationChoices: PersonalisationChoice[];
  selections?: {
    slots: SelectionSlot[];
  } | null;
}

export const imagePropertiesProcessing = async ({
  visData,
  personalisationChoices,
  selections = null
}: ImagePropertiesProcessingProps) => {
  // Track which _featured_image_X corresponds to which ALPHANUMERIC slot for later processing in addImageUrls.
  const featuredImageMapping = {};
  const featuredImageProperties = (
    await Promise.all(
      visData
        // Convert any urls to files.
        .map((vis, index) => {
          if (vis.file instanceof File) {
            return Promise.resolve({ ...vis, file: vis.file });
          }
          if (typeof vis.url === 'string') {
            return fetchImageAsFile(vis.url + '&w=1000', `featured-image-${index}.${imageType.split('/').at(1)}`).then(
              (file) => ({
                ...vis,
                file
              })
            );
          }
          // vis.url is a File
          return Promise.resolve({ ...vis, file: vis.url as File });
        })
    )
  ).reduce((acc, vis) => {
    const featuredImageKey = vis.lineItemPropertyKey;
    if (!featuredImageKey) return acc;

    acc[featuredImageKey] = vis.file || vis.url;
    // Find matching ALPHANUMERIC choice by groupId to track which slot this preview belongs to.
    const matchingAlphanumericChoice = personalisationChoices.find(
      (choice) => choice.slotType === 'ALPHANUMERIC' && choice.visualisationGroupId === vis.groupId
    );
    if (matchingAlphanumericChoice) {
      featuredImageMapping[featuredImageKey] = {
        groupId: vis.groupId,
        lineItemPropertyKey: matchingAlphanumericChoice.lineItemPropertyKey,
        supportingImageLineItemPropertyKey: matchingAlphanumericChoice.supportingImageLineItemPropertyKey,
        slotType: 'ALPHANUMERIC'
      };
    }
    return acc;
  }, {});

  const supportingImageProperties = selections?.slots?.reduce(
    (acc, slot) => {
      if (slot.croppedImage instanceof File) {
        const imageKey = slot.supportingImageLineItemPropertyKey ?? slot.lineItemPropertyKey;
        acc[`${imageKey}`] = slot.croppedImage;
      }
      return acc;
    },
    {} as Record<string, File>
  );

  const imageProps = {
    // Split visData into URL images (for featured_image) and File objects (for IMAGE_UPLOAD).
    // URLs go to _featured_image properties, Files will be added below.
    ...featuredImageProperties,
    // Add File objects from visData directly to properties.
    // This ensures the File objects are available for upload.
    // Use cropped image from slot if available, otherwise use the visData file.
    ...visData
      .filter((blob) => {
        // Filter for items where url is a File or file is a File
        const urlIsFile = typeof blob.url !== 'string' && blob.url instanceof File;
        const fileIsFile = blob.file instanceof File;
        return urlIsFile || fileIsFile;
      })
      .reduce(
        (acc, blob) => {
          // Find which IMAGE_UPLOAD choice this File belongs to by matching groupId with visualisationGroupId.
          let matchingChoice = personalisationChoices.find(
            (choice) => choice.slotType === 'IMAGE_UPLOAD' && choice.visualisationGroupId === blob.groupId
          );

          // Fallback: If no match found and there's only one IMAGE_UPLOAD choice, use it.
          // This supports legacy cases where visualisationGroup might not be set.
          if (!matchingChoice) {
            const imageUploadChoices = personalisationChoices.filter((choice) => choice.slotType === 'IMAGE_UPLOAD');
            if (imageUploadChoices.length === 1) {
              matchingChoice = imageUploadChoices[0];
            }
          }

          // Get the file from blob (either url or file property)
          let processedFile: File | null = null;
          if (blob.file instanceof File) {
            processedFile = blob.file;
          } else if (typeof blob.url !== 'string') {
            // At this point, url must be a File (filtered above)
            processedFile = blob.url as File;
          }

          // Note: We don't use cropped image here for the "- Visual" property
          // The cropped image is used for the "original" property (handled in supportingImageProperties above)

          if (!processedFile) {
            // Skip if we don't have a valid file
            return acc;
          }

          if (matchingChoice?.lineItemPropertyKey) {
            acc[matchingChoice.lineItemPropertyKey] = processedFile;
          }

          // Add additional feature images.
          if (blob.lineItemPropertyKey) {
            acc[blob.lineItemPropertyKey] = processedFile;
          }
          return acc;
        },
        {} as Record<string, File>
      ),
    // Add original uploaded images from IMAGE_UPLOAD choices as separate properties.
    // These are needed for warehouse processing and get their own CDN URLs.
    ...supportingImageProperties
  };

  return {
    featuredImageMapping,
    featuredImages: imageProps
  };
};
